import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { ArrowLeft, ArrowRight, MapPin, Users } from "lucide-react";
import { useLang } from "@/i18n";
import ProgrammeOfficiel from "@/components/site/ProgrammeOfficiel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function youtubeEmbed(url) {
  const m = (url || "").match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([\w-]+)/);
  return m ? `https://www.youtube.com/embed/${m[1]}` : null;
}

function useCountdown(target) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const diff = Math.max(0, new Date(target).getTime() - now);
  return {
    jours: Math.floor(diff / 86400000),
    heures: Math.floor(diff / 3600000) % 24,
    minutes: Math.floor(diff / 60000) % 60,
    secondes: Math.floor(diff / 1000) % 60,
  };
}

function BlurSoon({ children, testid, compact = false, active = true }) {
  if (!active) return <div data-testid={testid}>{children}</div>;
  return (
    <div className="relative overflow-hidden" data-testid={testid}>
      <div className="blur-md select-none pointer-events-none opacity-70" aria-hidden="true">{children}</div>
      <div className="absolute inset-0 flex items-center justify-center bg-[#0A0A0C]/40">
        <div className={`border border-[#C5A059]/60 bg-[#0A0A0C]/90 text-center ${compact ? "px-6 py-3" : "px-10 py-6"}`}>
          <p className={`font-display uppercase font-bold text-[#C5A059] tracking-[0.3em] ${compact ? "text-sm" : "text-2xl"}`}>Coming Soon</p>
        </div>
      </div>
    </div>
  );
}

export default function EventPage() {
  const [event, setEvent] = useState(null);
  const [partenaires, setPartenaires] = useState([]);
  const [detailsVisibles, setDetailsVisibles] = useState(false);
  const [logosVis, setLogosVis] = useState({ sponsor: false, club: false, partenaire: false });
  const [error, setError] = useState(false);
  const { t, lang } = useLang();

  useEffect(() => {
    axios.get(`${API}/event`).then((r) => setEvent(r.data)).catch(() => setError(true));
    axios.get(`${API}/settings`).then((r) => {
      setDetailsVisibles(!!r.data.details_visibles);
      setLogosVis({
        sponsor: !!r.data.logos_sponsors_visibles,
        club: !!r.data.logos_clubs_visibles,
        partenaire: !!r.data.logos_partenaires_visibles,
      });
    }).catch(() => {});
    axios.get(`${API}/logos`).then((r) => setPartenaires(r.data.map((l) => ({ nom: l.nom, type: l.categorie, logo: l.image })))).catch(() => {});
  }, []);

  const cd = useCountdown(event?.date_event || Date.now());

  if (error) {
    return (
      <div className="min-h-screen bg-[#0A0A0C] text-white flex flex-col items-center justify-center gap-6">
        <p className="text-[#71717A]">{t("eventpage.notfound")}</p>
        <Link to="/" className="text-[#C5A059] text-xs uppercase tracking-[0.25em] border border-[#C5A059] px-6 py-3">{t("eventpage.home")}</Link>
      </div>
    );
  }
  if (!event) {
    return <div className="min-h-screen bg-[#0A0A0C] flex items-center justify-center"><p className="text-[#71717A] text-xs uppercase tracking-[0.3em]">Chargement…</p></div>;
  }

  const dateLabel = new Date(event.date_event).toLocaleDateString(lang === "fr" ? "fr-FR" : lang === "es" ? "es-ES" : lang === "pt" ? "pt-PT" : "en-GB", { day: "numeric", month: "long", year: "numeric" });

  const phases = [
    { tag: t("eventpage.phase1.tag"), dates: t("eventpage.phase1.dates"), items: [t("eventpage.phase1.i1"), t("eventpage.phase1.i2"), t("eventpage.phase1.i3"), t("eventpage.phase1.i4"), t("eventpage.phase1.i5")], testid: "event-phase-1" },
    { tag: t("eventpage.phase2.tag"), dates: t("eventpage.phase2.dates"), items: [t("eventpage.phase2.i1"), t("eventpage.phase2.i2"), t("eventpage.phase2.i3"), t("eventpage.phase2.i4")], testid: "event-phase-2" },
  ];

  return (
    <div className="min-h-screen bg-[#0A0A0C] text-white grain">
      <header className="border-b border-[#27272A] bg-black/60 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" data-testid="event-logo-home"><img src="/assets/logo-dark-sm.png" alt="D2R" className="h-9 w-auto" /></Link>
          <Link to="/" data-testid="event-back-link" className="flex items-center gap-2 text-xs text-[#A1A1AA] hover:text-[#C5A059] transition-colors uppercase tracking-[0.2em]">
            <ArrowLeft size={14} /> {t("eventpage.back")}
          </Link>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 py-16 lg:py-24">
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}>
          <p className="text-[#C5A059] text-xs uppercase tracking-[0.4em] mb-6">{event.lieu} — {dateLabel}</p>
          <h1 className="font-display text-5xl md:text-7xl uppercase font-bold tracking-tight leading-[0.95]">
            {event.titre.split("2026")[0]}<span className="text-[#C5A059]">2026</span>
          </h1>
          <p className="flex items-center gap-3 text-[#A1A1AA] text-sm uppercase tracking-wider mt-6">
            <Users size={16} className="text-[#C5A059]" /> {event.sous_titre}
          </p>

          <div className="grid grid-cols-4 gap-4 max-w-xl mt-14" data-testid="event-countdown">
            {[[t("cd.days"), cd.jours], [t("cd.hours"), cd.heures], [t("cd.min"), cd.minutes], [t("cd.sec"), cd.secondes]].map(([label, val]) => (
              <div key={label} className="border border-[#27272A] bg-[#121214] py-6 text-center">
                <p className="font-display text-3xl md:text-4xl font-bold text-[#C5A059]">{String(val).padStart(2, "0")}</p>
                <p className="text-[10px] uppercase tracking-[0.25em] text-[#71717A] mt-2">{label}</p>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Les deux phases — dates visibles, détails floutés */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="mt-24"
          data-testid="event-phases"
        >
          <h2 className="font-display text-3xl uppercase font-bold mb-12">{t("eventpage.phases.a")} <span className="text-[#C5A059]">{t("eventpage.phases.b")}</span></h2>
          <div className="grid md:grid-cols-2 gap-8">
            {phases.map((phase) => (
              <div key={phase.testid} data-testid={phase.testid} className="border border-[#27272A] bg-[#121214] p-8 lg:p-10">
                <p className="text-[#C5A059] text-[10px] uppercase tracking-[0.35em]">{phase.tag}</p>
                <p className="font-display text-2xl uppercase font-bold mt-3">{phase.dates}</p>
                <BlurSoon testid={`${phase.testid}-blur`} compact active={!detailsVisibles}>
                  <ul className="mt-8 space-y-4">
                    {phase.items.map((item) => (
                      <li key={item} className="flex items-start gap-3 text-sm text-[#A1A1AA]">
                        <span className="w-1.5 h-1.5 bg-[#C5A059] mt-2 shrink-0" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </BlurSoon>
              </div>
            ))}
          </div>
        </motion.div>

        <div className="grid lg:grid-cols-12 gap-14 mt-24">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="lg:col-span-7"
          >
            <h2 className="font-display text-3xl uppercase font-bold mb-8">{t("eventpage.programme.full.a")} <span className="text-[#C5A059]">{t("eventpage.programme.full.b")}</span></h2>
            <BlurSoon testid="event-programme-blur" active={!detailsVisibles}>
              <div className="border-l border-[#27272A]" data-testid="event-programme">
                {event.programme.map((p, i) => (
                  <div key={i} className="relative pl-10 pb-10 last:pb-0">
                    <span className="absolute left-0 top-1.5 w-2.5 h-2.5 -translate-x-[5px] bg-[#C5A059]" />
                    <p className="text-[#C5A059] text-xs uppercase tracking-[0.25em]">{p.heure}</p>
                    <p className="text-lg text-white mt-2">{p.titre}</p>
                  </div>
                ))}
              </div>
            </BlurSoon>

            <h2 className="font-display text-3xl uppercase font-bold mt-20 mb-6">{t("eventpage.venue")}</h2>
            <BlurSoon testid="event-venue-blur" compact active={!detailsVisibles}>
              <div className="border border-[#27272A] bg-[#121214] p-8 flex items-start gap-4">
                <MapPin size={22} className="text-[#C5A059] shrink-0 mt-1" />
                <div>
                  <p className="font-display text-xl uppercase font-medium">{event.lieu}</p>
                  <p className="text-sm text-[#A1A1AA] mt-2">{event.adresse}</p>
                </div>
              </div>
            </BlurSoon>

            <BlurSoon testid="event-description-blur" compact active={!detailsVisibles}>
              <p className="text-[#A1A1AA] text-base leading-relaxed mt-10 max-w-xl">{event.description}</p>
            </BlurSoon>

            <Link
              to="/detection"
              data-testid="event-cta-register"
              className="group inline-flex items-center gap-3 mt-12 bg-[#C5A059] text-black px-8 py-4 text-xs uppercase tracking-[0.25em] font-semibold hover:bg-[#E5C07B] transition-colors duration-300"
            >
              {t("eventpage.cta")}
              <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.9, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
            className="lg:col-span-5 relative self-start"
          >
            <div className="absolute -inset-3 border border-[#C5A059]/40 translate-x-4 translate-y-4 pointer-events-none" />
            <img src={event.affiche} alt={event.titre} className="relative w-full border border-[#27272A]" data-testid="event-page-poster" />
          </motion.div>
        </div>

        {/* Partenaires & recruteurs — structure restaurée, détails floutés */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="mt-28"
          data-testid="event-partners"
        >
          <h2 className="font-display text-3xl uppercase font-bold mb-12">{t("eventpage.partners.a")} <span className="text-[#C5A059]">{t("eventpage.partners.b")}</span></h2>
          <div className="grid md:grid-cols-3 gap-10">
            {[
              ["sponsor", t("eventpage.sponsors")],
              ["club", t("eventpage.clubs")],
              ["partenaire", t("eventpage.partners")],
            ].map(([type, label]) => {
              const list = partenaires.filter((p) => p.type === type);
              const shown = list.length > 0 ? list : [{ nom: "Partenaire D2R", logo: null }, { nom: "Partenaire D2R", logo: null }, { nom: "Partenaire D2R", logo: null }];
              return (
                <BlurSoon key={type} testid={`event-partners-blur-${type}`} compact active={!logosVis[type]}>
                  <p className="text-[10px] uppercase tracking-[0.3em] text-[#C5A059] mb-5">{label}</p>
                  <div className="space-y-3">
                    {shown.map((p, idx) => (
                      <div key={`${p.nom}-${idx}`} className="flex items-center gap-4 border-b border-[#27272A] pb-3">
                        {p.logo && <img src={p.logo} alt={p.nom} className="h-8 w-auto object-contain grayscale" />}
                        <p className="font-display text-xl uppercase font-medium text-white">{p.nom}</p>
                      </div>
                    ))}
                  </div>
                </BlurSoon>
              );
            })}
          </div>
        </motion.div>

        {event.galerie && event.galerie.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="mt-28"
            data-testid="event-gallery"
          >
            <h2 className="font-display text-3xl uppercase font-bold mb-12">{t("eventpage.gallery.a")} <span className="text-[#C5A059]">{t("eventpage.gallery.b")}</span></h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {event.galerie.map((g, i) => {
                const yt = g.type === "youtube" ? youtubeEmbed(g.url) : null;
                return (
                  <figure key={i} className="border border-[#27272A] bg-[#121214] overflow-hidden" data-testid={`gallery-item-${i}`}>
                    {yt ? (
                      <div className="aspect-video">
                        <iframe src={yt} title={g.legende || "Vidéo"} className="w-full h-full" allowFullScreen />
                      </div>
                    ) : (
                      <img src={g.url} alt={g.legende || "Photo de l'événement"} loading="lazy" className="w-full aspect-[4/3] object-cover grayscale-[40%] hover:grayscale-0 hover:scale-105 transition-[filter,transform] duration-700" />
                    )}
                    {g.legende && <figcaption className="text-xs text-[#71717A] px-4 py-3">{g.legende}</figcaption>}
                  </figure>
                );
              })}
            </div>
          </motion.div>
        )}
      </div>
      <ProgrammeOfficiel />
    </div>
  );
}
